import React, {useEffect} from 'react';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import influencerActions from '../../actions/influencer.actions';
import {useGetMessages} from '../../hooks';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {RootState} from '../..';
import {selectAffiliateFinalPayout, selectAffiliateLoading} from '../../selectors';
import {Affiliate, FinalPayout} from '../../../shared/types';
import {withRHFormProvider} from '../../hocs';
import {formatCurrency} from '../../utils/Utils';
import {isPayoutThresholdEligible} from '../../utils/ValidationUtils';
import {CloseAccountContext} from './';
import useStyles from './CloseAccount.styles';

import {
    Header,
    AreYouSure,
    PayoutSelection,
    PayoutSelectionFieldValues,
    Disclaimer,
    DisclaimerFieldValues,
    Submit
} from './components';

export type CloseAccountFormFieldValues = PayoutSelectionFieldValues & DisclaimerFieldValues;

export interface CloseAccountProps {
    affiliate: Affiliate;
}

const CloseAccount = withRHFormProvider<CloseAccountProps, CloseAccountFormFieldValues>(
    ({affiliate, methods}) => {
        const styles = useStyles();
        const dispatch = useDispatch();
        const affiliateLoading = useSelector<RootState, boolean>(selectAffiliateLoading);
        const finalPayout = useSelector<RootState, FinalPayout>(
            selectAffiliateFinalPayout,
            shallowEqual
        );

        const messages = useGetMessages({
            backButton: 'epic.affiliate.common.goBack',
            header: 'epic.affiliate.closeAccount.header.v2'
        });

        useEffect(() => {
            dispatch(influencerActions.getFinalPayout());
        }, []);

        const {amount} = finalPayout || {};
        const hasEligibleBalance = isPayoutThresholdEligible(amount);
        const isRendering = typeof affiliateLoading !== 'boolean' || typeof amount !== 'number';
        const isLoading = affiliateLoading;
        const payoutTypeSelected = methods.watch('payoutType');

        const sections = [
            <AreYouSure key="AreYouSureSection" />,
            hasEligibleBalance && (
                <PayoutSelection
                    key="PayoutSelection"
                    payoutSetupInfo={affiliate && affiliate.payoutSetupInfo}
                />
            ),
            <Disclaimer key="DisclaimerSection" />,
            <Submit key="SubmitSection" />
        ];

        return (
            <CloseAccountContext.Provider
                value={{
                    isRendering,
                    payoutTypeSelected,
                    isLoading,
                    amount,
                    formattedAmount: formatCurrency(amount)
                }}>
                <Header backButtonLabel={messages.backButton} headerLabel={messages.header} />
                <Container maxWidth="md" className={styles.container}>
                    <form autoComplete="off">
                        {sections.map((section, index) => {
                            if (!section) return null;
                            return (
                                <Box key={index}>
                                    {section}
                                    {index + 1 < sections.length && (
                                        <Divider className={styles.divider} />
                                    )}
                                </Box>
                            );
                        })}
                    </form>
                </Container>
            </CloseAccountContext.Provider>
        );
    },
    {
        mode: 'onChange',
        shouldUnregister: true
    }
);

export default CloseAccount;
